import { VILLA_DRIVEWAY, VILLA_LAWN, VILLA_PHOTO_SPOTS, VILLA_ROOMS, VILLA_ROOM_ORDER, } from './villa.js';
import { insidePolygon } from './world-package/util.js';
const rectArea = (r) => [
    [r.x[0], r.z[0]],
    [r.x[1], r.z[0]],
    [r.x[1], r.z[1]],
    [r.x[0], r.z[1]],
];
/** The villa as a placement space (ADR-037's plan, unchanged): floors are its walkable rectangles. */
export const VILLA_SPACE = {
    world: 'villa',
    rooms: VILLA_ROOM_ORDER.map((id) => ({
        id,
        name: VILLA_ROOMS[id].name,
        area: rectArea(VILLA_ROOMS[id].rect),
        view: VILLA_ROOMS[id].view,
    })),
    begin: 'garage',
    floors: [...VILLA_ROOM_ORDER.map((id) => VILLA_ROOMS[id].rect), VILLA_DRIVEWAY, VILLA_LAWN].map((r) => ({ area: rectArea(r), maxSize: null })),
    tables: [],
    walls: [],
    start: { areas: [VILLA_ROOMS.garage.rect, VILLA_DRIVEWAY, VILLA_LAWN], from: 4.2, turn: 270 },
    photos: ['woonkamer', 'keuken', 'slaapkamer', 'terras'].map((id) => VILLA_PHOTO_SPOTS[id]),
    // furniture.ts FURNITURE_LIMITS.maxSize (not imported: furniture imports this module).
    maxSize: 4,
};
/** A package's plaatsruimte as a placement space. */
export function spaceOfBlock(world, block) {
    return {
        world,
        rooms: block.rooms,
        begin: block.begin,
        floors: block.floors,
        tables: block.tables,
        walls: block.walls,
        start: block.start,
        photos: block.photos,
        maxSize: block.maxSize,
    };
}
const clamp = (value, low, high) => Math.min(Math.max(value, low), high);
/** The rectangle a polygon is, when it is one with its sides along x and z (the villa's rooms are). */
function asRect(points) {
    if (points.length !== 4)
        return undefined;
    const xs = points.map((p) => p[0]);
    const zs = points.map((p) => p[1]);
    const x = [Math.min(...xs), Math.max(...xs)];
    const z = [Math.min(...zs), Math.max(...zs)];
    const corners = points.every((p) => (p[0] === x[0] || p[0] === x[1]) && (p[1] === z[0] || p[1] === z[1]));
    const distinct = new Set(points.map((p) => `${p[0]},${p[1]}`)).size === 4;
    return corners && distinct ? { x, z } : undefined;
}
const onEdge = (points, x, z) => points.some((_, i) => {
    const [ax, az] = points[(i + points.length - 1) % points.length];
    const [bx, bz] = points[i];
    const cross = (bx - ax) * (z - az) - (bz - az) * (x - ax);
    return (Math.abs(cross) < 1e-9 &&
        x >= Math.min(ax, bx) - 1e-9 &&
        x <= Math.max(ax, bx) + 1e-9 &&
        z >= Math.min(az, bz) - 1e-9 &&
        z <= Math.max(az, bz) + 1e-9);
});
/** The nearest point of a polygon (its edges included) to (x, z); itself when inside. */
function nearestIn(points, x, z) {
    const rect = asRect(points);
    if (rect)
        return [clamp(x, rect.x[0], rect.x[1]), clamp(z, rect.z[0], rect.z[1])];
    if (insidePolygon(points, x, z) || onEdge(points, x, z))
        return [x, z];
    let best = [x, z];
    let nearest = Number.POSITIVE_INFINITY;
    for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
        const [ax, az] = points[j];
        const [bx, bz] = points[i];
        const ex = bx - ax;
        const ez = bz - az;
        const t = clamp(((x - ax) * ex + (z - az) * ez) / Math.max(1e-12, ex * ex + ez * ez), 0, 1);
        const px = ax + ex * t;
        const pz = az + ez * t;
        const d = (px - x) ** 2 + (pz - z) ** 2;
        if (d < nearest) {
            nearest = d;
            best = [px, pz];
        }
    }
    return best;
}
/**
 * Where a piece may stand on the floor: `at` itself on a floor, else the nearest floor point. A
 * point that is not a number is taken as the origin.
 */
export function clampToSpace(space, at, size) {
    const x = Number.isFinite(at[0]) ? at[0] : 0;
    const z = Number.isFinite(at[1]) ? at[1] : 0;
    let best = [x, z];
    let nearest = Number.POSITIVE_INFINITY;
    // With a size, the floors that hold such a piece (their maxSize); when none does, any floor:
    // that a piece is too big for the space is the grab's refusal (`too-big`), not the clamp's.
    const holding = size ? space.floors.filter((floor) => fits(floor.maxSize, size)) : [];
    for (const floor of holding.length > 0 ? holding : space.floors) {
        const p = nearestIn(floor.area, x, z);
        const d = (p[0] - x) ** 2 + (p[1] - z) ** 2;
        if (d < nearest) {
            nearest = d;
            best = p;
            if (d === 0)
                break;
        }
    }
    return best;
}
/** The villa's floor: the nearest point of its rooms, driveway and lawn to `at` (ADR-037). */
export const clampToFloor = (at) => clampToSpace(VILLA_SPACE, at);
/** Between two pieces at the start; half of it is kept from a wall. */
export const START_GAP = 0.4;
const EPSILON = 1e-9;
function areaOf(rect, fromX) {
    const half = START_GAP / 2;
    return {
        x0: fromX ?? rect.x[0] + half,
        x1: rect.x[1] - half,
        z0: rect.z[0] + half,
        z1: rect.z[1] - half,
    };
}
/** The first place, row by row, where a footprint `dx` by `dz` fits; undefined when none does. */
function place(spaces, dx, dz) {
    for (const space of spaces) {
        const { area } = space;
        for (const shelf of space.shelves) {
            if (dx <= shelf.depth + EPSILON && shelf.zNext + dz <= area.z1 + EPSILON) {
                const at = [shelf.x0 + dx / 2, shelf.zNext + dz / 2];
                shelf.zNext += dz + START_GAP;
                return at;
            }
        }
        if (space.xNext + dx <= area.x1 + EPSILON && area.z0 + dz <= area.z1 + EPSILON) {
            const shelf = { x0: space.xNext, depth: dx, zNext: area.z0 + dz + START_GAP };
            space.shelves.push(shelf);
            space.xNext += dx + START_GAP;
            return [shelf.x0 + dx / 2, area.z0 + dz / 2];
        }
    }
    return undefined;
}
/** Whether a piece of this size (its largest side, a number that is not one counting as 0) fits `limit`. */
function fits(limit, size) {
    const largest = Math.max(0, ...size.filter((side) => Number.isFinite(side)));
    return limit === null || largest <= limit + 1e-9;
}
const cm = (metres) => Math.round(metres * 100) / 100 + 0;
/**
 * The start poses for pieces of these sizes ([width, height, depth], in the set's order), in the
 * same order: shelf-packed in rows across the first start area from `start.from`, deepest first,
 * 0.4 m apart, each turned `start.turn` (the villa's garage: to face the viewpoint); then the next
 * areas. A piece that fits nowhere any more starts the areas again, over the pieces already there:
 * every piece is placed on the floor, and pieces overlap only once all areas are full. Total: a
 * start with no room in it puts every piece at the first area's middle.
 */
export function startPoses(space, sizes) {
    const { start } = space;
    const areas = start.areas.map((rect, i) => areaOf(rect, i === 0 ? (start.from ?? undefined) : undefined));
    // The smallest side of any area: a piece bigger than that is packed as if it were that big.
    const largest = Math.max(0, Math.min(...areas.map((a) => Math.min(a.x1 - a.x0, a.z1 - a.z0))));
    const extent = (metres) => Number.isFinite(metres) && metres > 0 ? Math.min(metres, largest) : 0;
    const turn = ((Math.round(start.turn) % 360) + 360) % 360;
    const first = start.areas[0];
    const middle = first
        ? [(first.x[0] + first.x[1]) / 2, (first.z[0] + first.z[1]) / 2]
        : [0, 0];
    const fresh = () => areas.map((area) => ({ area, shelves: [], xNext: area.x0 }));
    // Turned 90° or 270°, a piece's depth lies along x and its width along z; turned 0° or 180°, the
    // other way round. (Only quarter turns are valid: the checker refuses the rest, and any other
    // number is read as the nearest quarter.)
    const sideways = Math.round(turn / 90) % 2 === 1;
    const feet = sizes.map(([width, , depth]) => sideways ? { dx: extent(depth), dz: extent(width) } : { dx: extent(width), dz: extent(depth) });
    const order = feet
        .map((_, i) => i)
        .sort((a, b) => feet[b].dx - feet[a].dx || feet[b].dz - feet[a].dz || a - b);
    const slots = new Array(sizes.length);
    let spaces = fresh();
    for (const i of order) {
        const { dx, dz } = feet[i];
        let at = place(spaces, dx, dz);
        if (at === undefined) {
            spaces = fresh();
            at = place(spaces, dx, dz) ?? middle;
        }
        slots[i] = { at: [cm(at[0]), cm(at[1])], turn: turn + 0 };
    }
    return slots;
}
/** How far from a wall a piece may be dropped and still hang on it, metres. */
const WALL_REACH = 0.6;
/**
 * A dropped or held piece's place (V6): on a table whose area holds it (y = the table's height), flat
 * against the nearest wall within 0.6 m (y within the wall's span), or on the nearest floor point at
 * y = 0. Only a pose that is raised (y above 1 cm) can go on a table or a wall, and only a piece that
 * fits that area's maxSize.
 */
export function placePose(space, pose, size) {
    const x = Number.isFinite(pose.at[0]) ? pose.at[0] : 0;
    const z = Number.isFinite(pose.at[1]) ? pose.at[1] : 0;
    const y = Number.isFinite(pose.y) ? pose.y : 0;
    if (y > 0.01) {
        for (const table of space.tables)
            if (Math.abs(y - table.height) <= 0.05 &&
                insidePolygon(table.area, x, z) &&
                fits(table.maxSize, size))
                return { at: [x, z], y: table.height };
        let best;
        let bestDistance = WALL_REACH;
        for (const wall of space.walls) {
            if (!fits(wall.maxSize, size))
                continue;
            const [ax, az] = wall.from;
            const ex = wall.to[0] - ax;
            const ez = wall.to[1] - az;
            const length = Math.hypot(ex, ez);
            if (!(length > 1e-9))
                continue;
            const t = clamp(((x - ax) * ex + (z - az) * ez) / (length * length), 0, 1);
            const px = ax + ex * t;
            const pz = az + ez * t;
            // The piece hangs half its depth out from the wall: it is its back face that must be within
            // reach, so a pose placePose itself made (centre half a depth out) is placed again as it is.
            const depth = Number.isFinite(size[2]) ? Math.max(0, size[2]) : 0;
            const d = Math.max(0, Math.hypot(x - px, z - pz) - depth / 2);
            if (d > bestDistance || (best !== undefined && d >= bestDistance))
                continue;
            // The side of the wall the pose is on; flat against it, half the piece's depth out.
            let nx = -ez / length;
            let nz = ex / length;
            if ((x - px) * nx + (z - pz) * nz < 0) {
                nx = -nx;
                nz = -nz;
            }
            const half = depth / 2;
            const top = Math.max(wall.low, wall.high - size[1]);
            bestDistance = d;
            best = {
                at: [cm(px + nx * half), cm(pz + nz * half)],
                y: cm(clamp(y, wall.low, top)),
            };
        }
        if (best)
            return best;
    }
    return { at: clampToSpace(space, [x, z], size), y: 0 };
}
//# sourceMappingURL=placement.js.map